'use client';
import React, { useState } from 'react';

interface FormData {
  name: string;
  email: string;
  phone: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  message?: string;
}

export default function ContactSection() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Full name is required.';
    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address.';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required.';
    } else if (!/^[6-9]\d{9}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Please enter a valid 10-digit Indian mobile number.';
    }
    if (!formData.message.trim()) newErrors.message = 'Please share your inquiry details.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      setSubmitted(true);
    }
  };

  const handleReset = () => {
    setFormData({ name: '', email: '', phone: '', message: '' });
    setErrors({});
    setSubmitted(false);
  };

  return (
    <section id="contact" className="py-16 md:py-20 bg-white">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="grid md:grid-cols-2 gap-12 items-start">

          {/* Left — Info */}
          <div>
            <h6 className="text-brand-turmeric font-avenir text-sm font-semibold uppercase tracking-widest mb-2">
              # Get In Touch
            </h6>
            <h2 className="text-3xl md:text-4xl font-bold text-brand-dark font-avenir mb-4 leading-tight">
              Start Your Investment Journey With Us
            </h2>
            <hr className="border-0 h-0.5 bg-brand-green w-20 mb-6" />
            <p className="text-brand-gray font-avenir text-sm leading-relaxed mb-8">
              Whether you're exploring premium plotted ventures near Hyderabad, seeking expert guidance on land investment, or looking to partner with our conglomerate — our team is ready to assist you at every step.
            </p>

            {/* Contact Details */}
            <div className="space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-brand-green/10 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-brand-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-brand-dark font-avenir text-sm font-bold mb-0.5">Our Office</p>
                  <p className="text-brand-gray font-avenir text-sm leading-relaxed">Neo Capital Group, Plot No 71, 3rd Floor, GAR Builders, Uppal Bhagayath, Uppal, Hyderabad, Telangana, India. Pincode: 500039</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-brand-turmeric/10 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-brand-turmeric" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <p className="text-brand-dark font-avenir text-sm font-bold mb-0.5">Email Us</p>
                  <p className="text-brand-gray font-avenir text-sm">info@neocapitalgroup.org</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-brand-green/10 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-brand-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div>
                  <p className="text-brand-dark font-avenir text-sm font-bold mb-0.5">Call Us</p>
                  <p className="text-brand-gray font-avenir text-sm">+91 7569962831</p>
                </div>
              </div>

              {/* Call Us removed */}
            </div>
          </div>

          {/* Right — Form */}
          <div className="bg-brand-gray-light/20 rounded-2xl p-8 border border-brand-green/10 shadow-card">
            {submitted ? (
              <div className="text-center py-10">
                <div className="w-16 h-16 rounded-full bg-brand-green/10 flex items-center justify-center mx-auto mb-5">
                  <svg className="w-8 h-8 text-brand-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-brand-dark font-avenir text-xl font-bold mb-2">Inquiry Received!</h3>
                <p className="text-brand-gray font-avenir text-sm leading-relaxed mb-6">
                  Thank you, <span className="font-semibold text-brand-dark">{formData.name}</span>. Our team will reach out to you within 24 hours to discuss your investment goals.
                </p>
                <button
                  onClick={handleReset}
                  className="inline-flex items-center gap-2 bg-brand-green text-white font-avenir text-sm font-semibold px-6 py-3 rounded-xl hover:bg-brand-green/90 transition-colors duration-200"
                >
                  Submit Another Inquiry
                </button>
              </div>
            ) : (
              <>
                <h3 className="text-brand-dark font-avenir text-lg font-bold mb-1">Contact Inquiry</h3>
                <p className="text-brand-gray font-avenir text-xs mb-6">Fill in your details and we'll get back to you shortly.</p>

                <form onSubmit={handleSubmit} noValidate className="space-y-5">
                  {/* Name */}
                  <div>
                    <label className="block text-brand-dark font-avenir text-xs font-semibold mb-1.5" htmlFor="contact-name">
                      Full Name <span className="text-brand-turmeric">*</span>
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="e.g. Arjun Sharma"
                      className={`w-full px-4 py-3 rounded-xl border font-avenir text-sm text-brand-dark placeholder-brand-gray/50 bg-white focus:outline-none focus:ring-2 focus:ring-brand-green/40 transition-all duration-200 ${errors.name ? 'border-red-400' : 'border-brand-green/20'}`}
                    />
                    {errors.name && <p className="text-red-500 font-avenir text-xs mt-1">{errors.name}</p>}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-brand-dark font-avenir text-xs font-semibold mb-1.5" htmlFor="contact-email">
                      Email Address <span className="text-brand-turmeric">*</span>
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="e.g. arjun@example.com"
                      className={`w-full px-4 py-3 rounded-xl border font-avenir text-sm text-brand-dark placeholder-brand-gray/50 bg-white focus:outline-none focus:ring-2 focus:ring-brand-green/40 transition-all duration-200 ${errors.email ? 'border-red-400' : 'border-brand-green/20'}`}
                    />
                    {errors.email && <p className="text-red-500 font-avenir text-xs mt-1">{errors.email}</p>}
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-brand-dark font-avenir text-xs font-semibold mb-1.5" htmlFor="contact-phone">
                      Phone Number <span className="text-brand-turmeric">*</span>
                    </label>
                    <input
                      id="contact-phone"
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="e.g. 9876543210"
                      className={`w-full px-4 py-3 rounded-xl border font-avenir text-sm text-brand-dark placeholder-brand-gray/50 bg-white focus:outline-none focus:ring-2 focus:ring-brand-green/40 transition-all duration-200 ${errors.phone ? 'border-red-400' : 'border-brand-green/20'}`}
                    />
                    {errors.phone && <p className="text-red-500 font-avenir text-xs mt-1">{errors.phone}</p>}
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-brand-dark font-avenir text-xs font-semibold mb-1.5" htmlFor="contact-message">
                      Your Inquiry <span className="text-brand-turmeric">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      placeholder="Tell us about your investment goals, preferred location, budget range, or any questions you have..."
                      className={`w-full px-4 py-3 rounded-xl border font-avenir text-sm text-brand-dark placeholder-brand-gray/50 bg-white focus:outline-none focus:ring-2 focus:ring-brand-green/40 transition-all duration-200 resize-none ${errors.message ? 'border-red-400' : 'border-brand-green/20'}`}
                    />
                    {errors.message && <p className="text-red-500 font-avenir text-xs mt-1">{errors.message}</p>}
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-brand-green text-white font-avenir text-sm font-bold py-3.5 rounded-xl hover:bg-brand-green/90 active:scale-[0.98] transition-all duration-200 tracking-wide"
                  >
                    Send Inquiry →
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
